import styled from '@emotion/styled';
import type { FabricEditor } from 'modules/fabric-editor';
import type { LayoutBlockData, LayoutBlockFitMode, LayoutPreset } from 'modules/manager/domain/layout-blocks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutBlocksModal } from '../layout-blocks-modal';
import CommitTextField from './inspectors/shared/commit-text-field';
import InspectorRow from './inspectors/shared/inspector-row';
import InspectorSection from './inspectors/shared/inspector-section';
import SelectField from './inspectors/shared/select-field';

interface LayoutBlocksPanelProps {
  editor: FabricEditor;
}

const FIT_MODE_OPTIONS = [
  { value: 'cover', label: 'Заполнение (обрезка)' },
  { value: 'contain', label: 'Вписать (без обрезки)' },
];

const LayoutBlocksPanel: React.FC<LayoutBlocksPanelProps> = ({ editor }) => {
  const facade = editor.layoutBlocks;
  const [data, setData] = useState<LayoutBlockData | undefined>(() => facade?.getData());
  const [showModal, setShowModal] = useState(false);

  const refresh = useCallback(() => {
    setData(facade?.getData());
  }, [facade]);

  useEffect(() => {
    if (!facade) return;
    refresh();
    const offAdded = editor.on('object:added', refresh);
    const offRemoved = editor.on('object:removed', refresh);
    const offModified = editor.on('object:modified', refresh);
    return () => {
      offAdded();
      offRemoved();
      offModified();
    };
  }, [editor, facade, refresh]);

  const presets: LayoutPreset[] = useMemo(() => facade?.listPresets() ?? [], [facade]);
  const currentPreset = useMemo(
    () => (data ? presets.find((p) => p.id === data.presetId) : undefined),
    [data, presets],
  );

  if (!facade) return null;

  if (!data) {
    return (
      <>
        <InspectorSection title="Блоки макета">
          <Hint>Нет фонового макета. Выберите пресет, чтобы добавить.</Hint>
          <PrimaryButton type="button" onClick={() => setShowModal(true)}>
            Добавить макет…
          </PrimaryButton>
        </InspectorSection>
        <LayoutBlocksModal
          show={showModal}
          onClose={() => setShowModal(false)}
          presets={presets}
          onPick={(id) => facade.apply(id)}
        />
      </>
    );
  }

  return (
    <>
      <InspectorSection title="Блоки макета">
        <InspectorRow label="Пресет">
          <Value>{currentPreset?.label ?? data.presetId}</Value>
        </InspectorRow>
        <Buttons>
          <SecondaryButton type="button" onClick={() => setShowModal(true)}>
            Заменить…
          </SecondaryButton>
          <DangerButton type="button" onClick={() => facade.remove()}>
            Удалить
          </DangerButton>
        </Buttons>
        <InspectorRow label="Прозрачность">
          <OpacityControl>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(data.opacity * 100)}
              onChange={(e) => facade.setOpacity(Number(e.target.value) / 100)}
            />
            <OpacityValue>{Math.round(data.opacity * 100)}%</OpacityValue>
          </OpacityControl>
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Блоки">
        {data.blocks.map((block, i) => (
          <BlockRow key={block.placeholderKey}>
            <BlockHeader>Блок {i + 1}</BlockHeader>
            <InspectorRow label="Подпись">
              <CommitTextField
                value={block.placeholderLabel}
                onCommit={(v) => facade.setBlockLabel(i, v)}
              />
            </InspectorRow>
            <InspectorRow label="Ключ">
              <KeyValue title={block.placeholderKey}>{block.placeholderKey}</KeyValue>
            </InspectorRow>
            <InspectorRow label="Вписывание">
              <SelectField
                value={block.fitMode}
                onChange={(v) => facade.setBlockFitMode(i, v as LayoutBlockFitMode)}
                options={FIT_MODE_OPTIONS}
              />
            </InspectorRow>
          </BlockRow>
        ))}
      </InspectorSection>
      <LayoutBlocksModal
        show={showModal}
        onClose={() => setShowModal(false)}
        presets={presets}
        currentPresetId={data.presetId}
        onPick={(id) => facade.apply(id)}
      />
    </>
  );
};

export default React.memo(LayoutBlocksPanel);

const Hint = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const Value = styled.span`
  font-size: 13px;
  color: #111827;
  font-weight: 500;
`;

const Buttons = styled.div`
  display: flex;
  gap: 6px;
`;

const baseButton = `
  flex: 1 1 auto;
  height: 30px;
  min-height: 30px;
  max-height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  ${baseButton}
  background: #4f46e5;
  color: #ffffff;
  border: none;

  &:hover {
    background: #4338ca;
  }
`;

const SecondaryButton = styled.button`
  ${baseButton}
  background: #f3f4f6;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #e5e7eb;
  }
`;

const DangerButton = styled.button`
  ${baseButton}
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;

  &:hover {
    background: #fee2e2;
  }
`;

const OpacityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  input[type='range'] {
    flex: 1;
    min-width: 0;
  }
`;

const OpacityValue = styled.span`
  font-size: 12px;
  color: #6b7280;
  min-width: 36px;
  text-align: right;
`;

const BlockRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 6px;
`;

const BlockHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
`;

const KeyValue = styled.span`
  font-size: 11px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
